import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Event as CalibrationIcon,
  Build as ToolIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import { RootState } from '../../store';
import {
  fetchToolFixturesStart,
  fetchStatisticsStart,
  fetchDropdownsStart,
  deleteToolFixtureStart,
  setSearchTerm,
  setCalibrationRequiredFilter,
  setActiveFilter,
  setSupplierFilter,
  setDepartmentFilter,
  setToolTypeFilter,
  setPage,
  setPageSize,
  clearError,
  setDetailModalOpen,
  setDeleteDialogOpen,
  setToolFixtureToDelete,
  fetchCalibrationEventsStart,
} from '../../store/reducers/toolFixtureSlice';
import { ToolFixture } from '../../services/toolFixtureApi';
import { toolFixtureApi } from '../../services/toolFixtureApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 'bold',
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  filtersCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  searchField: {
    minWidth: 250,
  },
  filterSelect: {
    minWidth: 150,
  },
  summaryCards: {
    marginBottom: theme.spacing(3),
  },
  summaryCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
    height: '100%',
  },
  summaryIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  summaryValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  statusChip: {
    fontWeight: 'bold',
  },
  calibrationChip: {
    fontWeight: 'bold',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  noDataContainer: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
}));

const ToolFixtureList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    toolFixtures,
    toolFixturesLoading,
    toolFixturesError,
    pagination,
    searchTerm,
    filters,
    statistics,
    suppliers,
    departments,
    toolTypes,
    deleteDialogOpen,
    toolFixtureToDelete,
    deleteToolFixtureLoading,
  } = useSelector((state: RootState) => state.toolFixture);

  const [selectedToolFixture, setSelectedToolFixture] = useState<ToolFixture | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    console.log('[ToolFixtureList] Component mounted, dispatching initial API calls...');
    
    // Add debugging for each API call
    console.log('[ToolFixtureList] Dispatching fetchToolFixturesStart...');
    dispatch(fetchToolFixturesStart());
    
    console.log('[ToolFixtureList] Dispatching fetchDropdownsStart...');
    dispatch(fetchDropdownsStart());
    
    // Note: fetchStatisticsStart was removed
    console.log('[ToolFixtureList] Initial API calls dispatched');
  }, [dispatch]);

  // Add debugging for state changes
  useEffect(() => {
    console.log('[ToolFixtureList] State update:', {
      toolFixturesLoading,
      toolFixturesError,
      toolFixturesLength: toolFixtures?.length || 0,
      suppliersLength: suppliers?.length || 0,
      departmentsLength: departments?.length || 0,
      toolTypesLength: toolTypes?.length || 0,
      pagination,
      searchTerm,
      filters
    });
  }, [toolFixtures, toolFixturesLoading, toolFixturesError, suppliers, departments, toolTypes, pagination, searchTerm, filters]);

  // Add debugging for errors
  useEffect(() => {
    if (toolFixturesError) {
      console.error('[ToolFixtureList] Error detected:', toolFixturesError);
      console.error('[ToolFixtureList] Error details:', {
        error: toolFixturesError,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    }
  }, [toolFixturesError]);

  // Handlers
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleCalibrationRequiredFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    dispatch(setCalibrationRequiredFilter(value === '' ? undefined : value === 'true'));
  };

  const handleActiveFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    dispatch(setActiveFilter(value === '' ? undefined : value === 'true'));
  };

  const handleSupplierFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(setSupplierFilter(event.target.value as string || undefined));
  };

  const handleDepartmentFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(setDepartmentFilter(event.target.value as string || undefined));
  };

  const handleToolTypeFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(setToolTypeFilter(event.target.value as string || undefined));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPageSize(parseInt(event.target.value, 10)));
  };

  const handleRefresh = () => {
    dispatch(fetchToolFixturesStart());
    dispatch(fetchStatisticsStart());
  };

  const handleAddNew = () => {
    navigate('/tools-fixtures/add');
  };

  const handleEdit = (toolFixture: ToolFixture) => {
    navigate(`/tools-fixtures/edit/${toolFixture.Id}`);
  };

  const handleView = (toolFixture: ToolFixture) => {
    setSelectedToolFixture(toolFixture);
    dispatch(setDetailModalOpen(true));
  };

  const handleCalibration = (toolFixture: ToolFixture) => {
    dispatch(fetchCalibrationEventsStart(toolFixture.Id));
    navigate(`/tools-fixtures/${toolFixture.Id}/calibration`);
  };

  const handleDelete = (toolFixture: ToolFixture) => {
    dispatch(setToolFixtureToDelete(toolFixture));
    dispatch(setDeleteDialogOpen(true));
  };

  const handleConfirmDelete = () => {
    if (toolFixtureToDelete) {
      dispatch(deleteToolFixtureStart(toolFixtureToDelete.Id));
    }
  };

  const handleCancelDelete = () => {
    dispatch(setDeleteDialogOpen(false));
    dispatch(setToolFixtureToDelete(null));
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const csvContent = toolFixtureApi.exportToCSV(toolFixtures);
      toolFixtureApi.downloadCSV(csvContent, `Tools-Fixtures-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleCloseDetailModal = () => {
    dispatch(setDetailModalOpen(false));
    setSelectedToolFixture(null);
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  // Utility functions
  const getCalibrationChip = (calibrationRequired: boolean) => {
    return (
      <Chip
        size="small"
        label={calibrationRequired ? 'Required' : 'Not Required'}
        className={classes.calibrationChip}
        color={calibrationRequired ? 'secondary' : 'default'}
        variant={calibrationRequired ? 'default' : 'outlined'}
      />
    );
  };

  const getStatusChip = (active: boolean) => {
    return (
      <Chip
        size="small"
        label={active ? 'Active' : 'Inactive'}
        className={classes.statusChip}
        color={active ? 'primary' : 'default'}
        variant={active ? 'default' : 'outlined'}
      />
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Tools & Fixtures
        </Typography>
        <Box className={classes.headerActions}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExportCSV}
            disabled={exportLoading}
          >
            {exportLoading ? <CircularProgress size={20} /> : 'Export CSV'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add Tool/Fixture
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      {statistics && (
        <Grid container spacing={3} className={classes.summaryCards}>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <ToolIcon className={classes.summaryIcon} color="primary" />
                <Typography className={classes.summaryValue}>
                  {statistics.totalTools}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Total Tools
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <ToolIcon className={classes.summaryIcon} style={{ color: '#4caf50' }} />
                <Typography className={classes.summaryValue}>
                  {statistics.activeTools}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Active Tools
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <CalibrationIcon className={classes.summaryIcon} style={{ color: '#ff9800' }} />
                <Typography className={classes.summaryValue}>
                  {statistics.toolsRequiringCalibration}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Require Calibration
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <CalibrationIcon className={classes.summaryIcon} style={{ color: '#2196f3' }} />
                <Typography className={classes.summaryValue}>
                  {statistics.upcomingCalibrations}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Upcoming Calibrations
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <CalibrationIcon className={classes.summaryIcon} style={{ color: '#f44336' }} />
                <Typography className={classes.summaryValue}>
                  {statistics.overdueMaintenance}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Overdue Maintenance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <ToolIcon className={classes.summaryIcon} style={{ color: '#9c27b0' }} />
                <Typography className={classes.summaryValue}>
                  {statistics.averageLeadTime}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Avg. Lead Time (Days)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card className={classes.filtersCard}>
        <Box className={classes.filtersContainer}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="Search by TF Number or Description..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon color="action" />,
            }}
          />
          
          <FormControl className={classes.filterSelect} size="small" variant="outlined">
            <InputLabel>Calibration Required</InputLabel>
            <Select
              value={filters.calibrationRequired === undefined ? '' : filters.calibrationRequired.toString()}
              onChange={handleCalibrationRequiredFilterChange}
              label="Calibration Required"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Required</MenuItem>
              <MenuItem value="false">Not Required</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.filterSelect} size="small" variant="outlined">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.active === undefined ? '' : filters.active.toString()}
              onChange={handleActiveFilterChange}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.filterSelect} size="small" variant="outlined">
            <InputLabel>Supplier</InputLabel>
            <Select
              value={filters.supplierId || ''}
              onChange={handleSupplierFilterChange}
              label="Supplier"
            >
              <MenuItem value="">All Suppliers</MenuItem>
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.Id} value={supplier.Id}>
                  {supplier.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={classes.filterSelect} size="small" variant="outlined">
            <InputLabel>Department</InputLabel>
            <Select
              value={filters.departmentId || ''}
              onChange={handleDepartmentFilterChange}
              label="Department"
            >
              <MenuItem value="">All Departments</MenuItem>
              {departments.map((department) => (
                <MenuItem key={department.Id} value={department.Id}>
                  {department.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={classes.filterSelect} size="small" variant="outlined">
            <InputLabel>Tool Type</InputLabel>
            <Select
              value={filters.toolTypeId || ''}
              onChange={handleToolTypeFilterChange}
              label="Tool Type"
            >
              <MenuItem value="">All Types</MenuItem>
              {toolTypes.map((toolType) => (
                <MenuItem key={toolType.Id} value={toolType.Id}>
                  {toolType.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>TF Number</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Tool Type</TableCell>
              <TableCell>Price/Unit</TableCell>
              <TableCell>Lead Time</TableCell>
              <TableCell>Min Stock</TableCell>
              <TableCell>Calibration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {toolFixturesLoading && (!toolFixtures || toolFixtures.length === 0) ? (
              <TableRow>
                <TableCell colSpan={11}>
                  <Box className={classes.loadingContainer}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (!toolFixtures || toolFixtures.length === 0) ? (
              <TableRow>
                <TableCell colSpan={11}>
                  <Box className={classes.noDataContainer}>
                    <ToolIcon style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                      No Tools & Fixtures Found
                    </Typography>
                    <Typography color="textSecondary">
                      {searchTerm || Object.values(filters).some(f => f !== undefined)
                        ? 'Try adjusting your search or filters'
                        : 'Get started by adding your first tool or fixture'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              (toolFixtures || []).map((toolFixture) => (
                <TableRow key={toolFixture.Id} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {toolFixture.TFNum}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {toolFixture.Description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {toolFixture.Supplier?.Name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {toolFixture.Department?.Name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {toolFixture.ToolType?.Name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatCurrency(toolFixture.PricePerUnit)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {toolFixture.LeadTime ? `${toolFixture.LeadTime} days` : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {toolFixture.MinStockLevel}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getCalibrationChip(toolFixture.CalibrationRequired)}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(toolFixture.Active)}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleView(toolFixture)}
                        color="default"
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Tool/Fixture">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(toolFixture)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {toolFixture.CalibrationRequired && (
                      <Tooltip title="Calibration Schedule">
                        <IconButton
                          size="small"
                          onClick={() => handleCalibration(toolFixture)}
                          style={{ color: '#ff9800' }}
                        >
                          <CalibrationIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete Tool/Fixture">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(toolFixture)}
                        style={{ color: '#f44336' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={pagination.totalCount}
        rowsPerPage={pagination.pageSize}
        page={pagination.pageNum - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete tool/fixture "{toolFixtureToDelete?.TFNum}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="secondary"
            disabled={deleteToolFixtureLoading}
          >
            {deleteToolFixtureLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!toolFixturesError}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={toolFixturesError}
      />
    </Box>
  );
};

export default ToolFixtureList; 